import { FcGeneratorOptions, optionsDefaults } from '@dtx-company/flowcode-generator-types/src'
import { fcGenerateFlowcode } from './fcGenerateFlowcode'
import { useObjectURL } from './hooks/useObjectUrl'
import React, { useRef } from 'react'

export interface FlowcodeProps {
  alt?: string
  containerCx?: string
  onChange?: () => void
  options: FcGeneratorOptions
  renderLoadingIcon?: () => JSX.Element
  staticOptimization?: boolean // use only for fixed code previews
}

export const Flowcode = ({
  alt = 'Flowcode',
  staticOptimization = false,
  options,
  renderLoadingIcon,
  onChange,
  ...props
}: FlowcodeProps): JSX.Element => {
  // when staticOptimization = true, used with a renderLoadingIcon which returns
  // a static image of a flowcode, we can defer loading the full
  // generator until a logoImageUrl with a blob: protocol is referenced
  const loadGenerator = useRef(!(renderLoadingIcon && staticOptimization))
  const { objectURL, setObject } = useObjectURL()
  const targetSize = options.targetSize || optionsDefaults.targetSize
  const optionsString = JSON.stringify(options)
  const logoImageUrl = options.logoImageUrl

  React.useEffect(() => {
    let isActive = true
    if (logoImageUrl?.startsWith('blob:')) {
      loadGenerator.current = true
    }
    async function generateFlowCode(): Promise<void> {
      const generatorOptions: FcGeneratorOptions = JSON.parse(optionsString)
      const result = await fcGenerateFlowcode(generatorOptions)
      if (!isActive) return
      result.setViewBox(`0 0 ${targetSize} ${targetSize}`)
      const blob = new Blob([result.getDom()], { type: 'image/svg+xml' })
      setObject(blob)
      onChange?.()
    }
    if (loadGenerator.current) {
      generateFlowCode()
    }
    return () => {
      isActive = false
    }
  }, [logoImageUrl, targetSize, onChange, optionsString, setObject])

  if (!objectURL)
    return renderLoadingIcon ? renderLoadingIcon() : <div className={props.containerCx} />

  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      alt={alt}
      className={props.containerCx}
      src={objectURL}
      style={{ width: '100%', height: '100%' }}
    />
  )
}
